import { faArrowRight, faCheck } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  SPACING,
  Typography,
  Button,
  FamilyChildProfile,
  UserRole,
  JoonUIColor,
  TextButton,
} from "@joonapp/web-shared"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import { useDownloadMobileAppModal } from "./DownloadMobileAppModal"
import { useFamilyQuery, useSubscriberInfo } from "../../networking/queries"
import { useGetChildStartedModal } from "../../pages/family/GetChildStartedModal"
import { FamilyQuerySelect, SubscribedStatus } from "../../types"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { localStorageItems } from "../../util/storage"
import { getChildImage, getCurrentSubscription } from "../../util/util"
import Card from "../card/Card"
import { ChildTestimonialCarousel } from "../carousel/ChildTestimonialCarousel"
import SlideUpModal from "../slideUpModal/SlideUpModal"

interface SetUpChildFTUXModalStore {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
}

export const useSetUpChildFTUXModal = create<SetUpChildFTUXModalStore>(
  (set) => ({
    isOpen: false,
    onOpen: () => {
      trackAnalyticEvent(ANALYTIC_EVENTS.SHOW_FORCE_ONBOARD)
      set({ isOpen: true })
    },
    onClose: () => {
      localStorage.removeItem(localStorageItems.needsToSeeWelcomeModal)
      set({ isOpen: false })
    },
  })
)

export const SetUpChildFTUXModal = () => {
  const { isOpen, onClose } = useSetUpChildFTUXModal()
  const { t } = useTranslation()
  const { onOpen: onOpenDownloadMobileAppModal } = useDownloadMobileAppModal()
  const { data: children } = useFamilyQuery(FamilyQuerySelect.CHILDREN)
  const { data: subscriberInfo } = useSubscriberInfo()
  const currentSubscription = getCurrentSubscription(subscriberInfo)
  const hasTrial =
    currentSubscription && currentSubscription.period_type === "trial"

  const everyChildIsSetUp = children?.every((child) => {
    if (child.role === UserRole.CHILD) {
      return child.user.active_doter.date_hatched !== null
    }
    return true
  })

  const handleDoThisLater = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.FORCE_ONBOARD_LATER)
    onOpenDownloadMobileAppModal(
      hasTrial ? SubscribedStatus.SUBSCRIBED : SubscribedStatus.NOT_SUBSCRIBED
    )
    localStorage.removeItem(localStorageItems.hasSeenReferralFTUXModal)
    onClose()
  }

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={onClose}
      size="fit-content"
      backgroundType="overlay"
      desktopWidth="400px"
      mandatory
    >
      <div className="flex flex-col h-full overflow-auto pb-6">
        <div className="flex flex-col gap-2 p-6 pb-4">
          <Typography variant="h3" textAlign="center">
            {t("SETUP_CHILD_TITLE")}
          </Typography>
          <Typography variant="bodySmall" textAlign="center">
            {t("SETUP_CHILD_SUBTITLE")}
          </Typography>
        </div>
        <div
          className="w-full flex flex-col gap-4 p-4"
          style={{
            background:
              "linear-gradient(291deg, rgba(152, 242, 219, 0.40) 10.3%, rgba(36, 155, 238, 0.40) 91.68%), #FFF",
          }}
        >
          <Typography variant="bodyBold" textAlign="center" className="px-6">
            {t("SETUP_CHILD_TESTIMONIALS_TITLE")}
          </Typography>
          <ChildTestimonialCarousel />
        </div>
        <div className="flex flex-col gap-4 p-6 pt-4">
          <Typography variant="h4" textAlign="center">
            {t("SETUP_CHILD_SELECT")}
          </Typography>
          <div className="flex flex-col gap-2">
            {children?.map((child) => (
              <ChildCard key={child.user.id} child={child} />
            ))}
          </div>
        </div>
        <div className="flex w-full justify-center px-6">
          {everyChildIsSetUp ? (
            <Button text={t("FTUX_DONE")} onClick={onClose} fullWidth />
          ) : (
            <TextButton
              onClick={handleDoThisLater}
              style={{ width: "fit-content" }}
            >
              <Typography variant="body" color={JoonUIColor.text.primaryAccent}>
                {t("SETUP_CHILD_DO_LATER")}
              </Typography>
            </TextButton>
          )}
        </div>
      </div>
    </SlideUpModal>
  )
}

const ChildCard = ({ child }: { child: FamilyChildProfile }) => {
  const { t } = useTranslation()
  const { onOpen: onOpenGetChildStartedModal } = useGetChildStartedModal()
  const notYetOnboarded =
    child.role === UserRole.CHILD &&
    child?.user.active_doter.date_hatched === null

  return (
    <Card
      title={
        <div className="flex flex-row gap-2 items-center">
          <img
            src={getChildImage(child.user) || ""}
            alt="Child avatar"
            style={{
              width: SPACING.space6,
              height: SPACING.space6,
              borderRadius: SPACING.space6,
            }}
          />
          <Typography variant="bodySmallBold">{child.user.name}</Typography>
        </div>
      }
      buttonComponent={
        notYetOnboarded ? (
          <Button
            size="small"
            rightIcon={notYetOnboarded ? faArrowRight : undefined}
            text={t("SETUP_CHILD_START")}
            onClick={() => {
              trackAnalyticEvent(ANALYTIC_EVENTS.OPEN_DOWNLOAD_INSTRUCTIONS)
              onOpenGetChildStartedModal(child.user.id)
            }}
            style={{ width: "70px", whiteSpace: "nowrap" }}
          />
        ) : (
          <div className="flex flex-row gap-2 items-center">
            <Typography
              variant="bodySmall"
              color={JoonUIColor.semantic.success}
            >
              {t("SETUP_CHILD_SET_UP")}
            </Typography>
            <FontAwesomeIcon
              icon={faCheck}
              color={JoonUIColor.semantic.success}
              fontSize={14}
            />
          </div>
        )
      }
      type="primary"
      style={{
        background: JoonUIColor.background.primaryNeutral,
        transition: "none",
        transform: "none",
      }}
    />
  )
}

export default SetUpChildFTUXModal
